/**
 * The command -> Jitsi iframe-API bridge, ported essentially verbatim from
 * spot-client `JitsiMeetingFrame._onMeetingCommand`. This is the heart of "it IS
 * Spot": a Remote's `ControlEnvelope` command frame, relayed by room-do to the TV,
 * lands here and becomes a `JitsiMeetExternalAPI.executeCommand(...)` call — the
 * exact pipeline the legacy XMPP path drove, now over the DO WebSocket.
 *
 * The cached toggle state (`MeetingToggleState`) preserves the legacy behaviour of
 * comparing the desired state to the current one so a toggle command is a no-op
 * when already in the target state (Jitsi's API exposes toggles, not setters).
 */

import {
    COMMANDS,
    type CommandName
} from '@spaces/contracts';
import { SET_RECORDING } from '../commands.js';
import { log } from '../log.js';

/** A minimal view of the Jitsi external API surface we drive. */
export interface JitsiApi {
    executeCommand(command: string, ...args: unknown[]): void;
    sendProxyConnectionEvent?(data: unknown): void;
}

/** Cached toggle state, mirroring spot's `_isAudioMuted` et al. */
export interface MeetingToggleState {
    /** Last state the iframe REPORTED for the local audio track. */
    audioMuted: boolean;
    /**
     * The audio-mute the TV was last COMMANDED into by a Remote `setAudioMute`
     * (`null` until the first such command). The TV has no local mic control, so this
     * is the authoritative intent: an `audioMuteStatusChanged` echo that contradicts a
     * non-null intent is a spurious renegotiation artefact (Jitsi recreated the local
     * track unmuted) and is re-asserted rather than surfaced. See JitsiMeetingFrame.
     */
    audioMuteIntent: boolean | null;
    videoMuted: boolean;
    tileView: boolean;
    screensharing: boolean;
    handRaised: boolean;
    whiteboardOpen: boolean;
    recording: boolean;
    /** Whether the subtitles/captions pane is showing (Jitsi emits no event, latched on command). */
    subtitlesOn: boolean;
    /** Whether the meeting lobby is enabled (latched on command; no echo event). */
    lobbyEnabled: boolean;
    /**
     * Whether the Jitsi chat panel is open on the TV. Unlike the latched-only toggles
     * above, this is reconciled by the iframe's `chatUpdated` event (`isOpen`); it is
     * also set optimistically on command in case that echo is delayed, so a no-arg
     * `toggleChat` never accidentally HIDES an already-open panel. The chat panel is
     * how the ROOM reads the conversation (incoming + outgoing) on the big screen.
     */
    chatOpen: boolean;
}

/** A fresh toggle state (everything off — the join default). */
export function initialToggleState(): MeetingToggleState {
    return {
        audioMuted: false,
        audioMuteIntent: null,
        videoMuted: false,
        tileView: false,
        screensharing: false,
        handRaised: false,
        whiteboardOpen: false,
        recording: false,
        subtitlesOn: false,
        lobbyEnabled: false,
        chatOpen: false
    };
}

/** The outcome of applying a command — used to build the ack. */
export interface CommandResult {
    ok: boolean;
    error?: string;
}

type CommandPayload = Record<string, unknown> | undefined;

function bool(payload: CommandPayload, key: string): boolean {
    return Boolean(payload?.[key]);
}

/**
 * Apply a single Remote command to the meeting. Returns whether it was handled;
 * the caller turns this into the `ack` frame correlated back to the Remote.
 *
 * `onMeetingLeave` is invoked for the legacy skipFeedback hangup shortcut. Note
 * `GO_TO_MEETING` is intentionally NOT handled here — it is an app-level
 * navigation (which meeting the TV hosts), handled by the TV app, not the frame.
 */
export function applyMeetingCommand(
    api: JitsiApi,
    state: MeetingToggleState,
    command: CommandName | typeof SET_RECORDING,
    payload: CommandPayload,
    onMeetingLeave: () => void
): CommandResult {
    switch (command) {
    case COMMANDS.ADJUST_VOLUME:
        // Native (Electron host) only — a no-op in the browser TV. Logged, acked ok.
        log.log('adjustVolume (native-only, no-op in browser)', payload);
        return { ok: true };

    case COMMANDS.HANG_UP:
        api.executeCommand('hangup');
        if (bool(payload, 'skipFeedback')) {
            onMeetingLeave();
        }
        return { ok: true };

    case COMMANDS.GRANT_RECORDING_CONSENT:
        api.executeCommand('grantRecordingConsent', bool(payload, 'unmute'));
        return { ok: true };

    case COMMANDS.SEND_TOUCH_TONES:
        api.executeCommand('sendTones', { tones: String(payload?.tones ?? '') });
        return { ok: true };

    case COMMANDS.SET_AUDIO_MUTE: {
        const mute = bool(payload, 'mute');

        // Record the commanded intent so the iframe's `audioMuteStatusChanged` echo can
        // tell a legitimate change from a spurious renegotiation one (see the handler).
        state.audioMuteIntent = mute;
        if (state.audioMuted !== mute) {
            api.executeCommand('toggleAudio');
        }
        return { ok: true };
    }

    case COMMANDS.SET_VIDEO_MUTE: {
        const mute = bool(payload, 'mute');

        if (state.videoMuted !== mute) {
            api.executeCommand('toggleVideo');
        }
        return { ok: true };
    }

    case COMMANDS.SET_RAISE_HAND: {
        const raised = bool(payload, 'raised');

        if (state.handRaised !== raised) {
            api.executeCommand('toggleRaiseHand');
        }
        return { ok: true };
    }

    case COMMANDS.SET_SCREENSHARING: {
        const on = bool(payload, 'on');

        if (state.screensharing !== on) {
            api.executeCommand('toggleShareScreen', { enable: on });
        }
        return { ok: true };
    }

    case COMMANDS.SET_TILE_VIEW: {
        const tileView = bool(payload, 'tileView');

        // Use the deterministic setter, not `toggleTileView`: a toggle drifts when the
        // TV's cached state and the Remote's intent disagree (e.g. Jitsi suppressed a
        // tile change while the TV was alone, so no `tileViewChanged` echo arrived).
        // `setTileView(enabled)` always lands the intended layout.
        api.executeCommand('setTileView', tileView);
        state.tileView = tileView;
        return { ok: true };
    }

    case COMMANDS.SET_WHITEBOARD: {
        const visible = bool(payload, 'visible');

        if (state.whiteboardOpen !== visible) {
            api.executeCommand('toggleWhiteboard', visible);
        }
        // Jitsi emits no whiteboard-status event, so latch the intent here to keep the
        // toggle dedup consistent within the session.
        state.whiteboardOpen = visible;
        return { ok: true };
    }

    case COMMANDS.SET_SUBTITLES: {
        const on = bool(payload, 'on');

        // Jitsi exposes only the no-arg `toggleSubtitles`, so dedup against the latched
        // state (there is no subtitles-status event to drive presence) — the same shape
        // as SET_WHITEBOARD. This shows/hides the captions pane IN THE ROOM; caption
        // TEXT is never streamed to the Remote.
        if (state.subtitlesOn !== on) {
            api.executeCommand('toggleSubtitles');
        }
        state.subtitlesOn = on;
        return { ok: true };
    }

    case COMMANDS.PIN_PARTICIPANT: {
        // Pin a participant to the main stage; an empty id unpins everyone (Jitsi's
        // documented `pinParticipant()` behaviour). `videoType` is left default.
        const participantId = String(payload?.participantId ?? '');

        api.executeCommand('pinParticipant', participantId || undefined);
        return { ok: true };
    }

    case COMMANDS.MUTE_EVERYONE: {
        // Moderator action (gated on the Remote UI). Jitsi excludes the local
        // moderator, so the TV itself is never muted. Defaults to audio.
        const mediaType = payload?.mediaType === 'video' ? 'video' : 'audio';

        api.executeCommand('muteEveryone', mediaType);
        return { ok: true };
    }

    case COMMANDS.SET_LOBBY: {
        // Moderator action (gated on the Remote UI). Enable/disable the knocking lobby.
        const enabled = bool(payload, 'enabled');

        api.executeCommand('toggleLobby', enabled);
        state.lobbyEnabled = enabled;
        return { ok: true };
    }

    case COMMANDS.SEND_CHAT_MESSAGE: {
        // Any participant may chat — NOT moderator-gated. `sendChatMessage` with a
        // single string arg sends a PUBLIC message (no recipient → cleared private
        // recipient, verified against the shipped 8x8.vc external_api). An
        // empty/whitespace message is a no-op rather than a dead frame.
        const text = String(payload?.text ?? '').trim();

        if (!text) {
            return { ok: false, error: 'empty chat message' };
        }
        api.executeCommand('sendChatMessage', text);

        // Read-on-the-TV: surface the conversation so the room can read it. Jitsi's
        // chat panel renders the whole thread; open it if it isn't already. `toggleChat`
        // is a no-arg toggle, so only fire when closed (else we'd hide it). `chatOpen`
        // is reconciled by the TV's `chatUpdated` listener; latch optimistically here
        // in case that echo is delayed.
        if (!state.chatOpen) {
            api.executeCommand('toggleChat');
            state.chatOpen = true;
        }
        return { ok: true };
    }

    case COMMANDS.SET_CHAT_OPEN: {
        // Explicit open/close of the room's chat panel, independent of sending — the
        // read-only twin of SEND_CHAT_MESSAGE. Jitsi exposes only the no-arg
        // `toggleChat`, so dedup against the desired state (same shape as
        // SET_WHITEBOARD / SET_SUBTITLES) — fire the toggle ONLY when the panel isn't
        // already where we want it, so a deterministic open never accidentally hides
        // an open panel and vice-versa. `chatOpen` is reconciled by the TV's
        // `chatUpdated` listener; latch the intent optimistically in case that echo
        // is delayed.
        const open = bool(payload, 'open');

        if (state.chatOpen !== open) {
            api.executeCommand('toggleChat');
        }
        state.chatOpen = open;
        return { ok: true };
    }

    case COMMANDS.SUBMIT_FEEDBACK:
        api.executeCommand('submitFeedback', payload);
        return { ok: true };

    case COMMANDS.SUBMIT_PASSWORD:
        api.executeCommand('password', String(payload?.password ?? ''));
        return { ok: true };

    case SET_RECORDING: {
        // Room-initiated recording (Phase-1). Moderator-gated on the Remote UI.
        const on = bool(payload, 'on');

        if (on) {
            api.executeCommand('startRecording', { mode: 'file' });
        } else {
            api.executeCommand('stopRecording', 'file');
        }
        state.recording = on;
        return { ok: true };
    }

    default:
        log.warn('unhandled meeting command', { command });
        return { ok: false, error: `unhandled command: ${String(command)}` };
    }
}
